import { useEffect, useMemo, useState } from 'react';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '../../lib/format';
import ProfileAvatar from '../common/ProfileAvatar';
import { useApp } from '../../context/AppContext';
import { usePdpa } from '../../context/PdpaContext';

export default function Sidebar() {
  const {
    activePage,
    currentUser,
    isPageVisible,
    logout,
    showPage,
    sidebarOpen,
    sidebarCollapsed,
    toggleSidebarCollapsed,
  } = useApp();
  const { isPdpaEnabled } = usePdpa();
  const [clock, setClock] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setClock(now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    };

    update();
    const intervalId = setInterval(update, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const navigation = useMemo(() => ([
    {
      label: 'ภาพรวม',
      items: [
        { page: 'dashboard', icon: 'fa-solid fa-chart-pie', title: 'แดชบอร์ด' },
      ],
    },
    {
      label: 'ทะเบียนระบบ',
      items: [
        { page: 'registry', icon: 'fa-solid fa-server', title: 'รายการระบบทั้งหมด' },
        { page: 'register-form', icon: 'fa-solid fa-plus', title: 'ขึ้นทะเบียนระบบใหม่' },
        { page: 'import-systems', icon: 'fa-solid fa-file-import', title: 'นำเข้าจาก Excel' },
        { page: 'access-request', icon: 'fa-solid fa-key', title: 'ขอสิทธิ์ / Reset Password' },
      ],
    },
    {
      label: 'พนักงาน เข้า/ออก',
      items: [
        { page: 'onboard', icon: 'fa-solid fa-user-plus', title: 'เข้างานใหม่ (Onboard)' },
        { page: 'offboard', icon: 'fa-solid fa-user-minus', title: 'พ้นสภาพ (Offboard)' },
      ],
    },
    {
      label: 'บัญชีของฉัน',
      items: [
        { page: 'privacy-settings', icon: 'fa-solid fa-shield-halved', title: 'นโยบายส่วนตัวและคุกกี้' },
      ],
    },
    {
      label: 'จัดการระบบ',
      items: [
        { page: 'permissions', icon: 'fa-solid fa-shield-halved', title: 'จัดการสิทธิ์ / Role' },
        { page: 'smtp-settings', icon: 'fa-solid fa-envelope', title: 'ตั้งค่า SMTP' },
        { page: 'checklist-settings', icon: 'fa-solid fa-list-check', title: 'ตั้งค่า Checklist' },
        { page: 'exit-interview-settings', icon: 'fa-solid fa-comments', title: 'ตั้งค่า Exit Interview' },
      ],
    },
  ]), []);

  const isNavItemVisible = (page) => {
    if (!isPageVisible(page)) return false;
    if (page === 'privacy-settings' && !isPdpaEnabled) return false;
    return true;
  };

  const NavItem = ({ page, icon, title }) => {
    if (!isNavItemVisible(page)) return null;

    return (
      <button
        type="button"
        className={`nav-item-btn ${activePage === page ? 'active' : ''}`}
        onClick={() => showPage(page)}
      >
        <span className="nav-icon"><i className={icon}></i></span>
        {title}
      </button>
    );
  };

  return (
    <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
      <div className="sidebar-logo">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ minWidth: 0 }}>
            <div className="logo-badge"><div className="logo-dot"></div> BSR · SYSREG</div>
            <div className="logo-sub">ระบบทะเบียนระบบและสิทธิ์</div>
          </div>
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleSidebarCollapsed}
            title="พับเมนู"
            aria-label="พับเมนู"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
        </div>
      </div>

      <div className="sidebar-scroll">
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,.3)', marginBottom: '8px', fontFamily: "'IBM Plex Mono',monospace" }}>
            ผู้ใช้งานปัจจุบัน
          </div>
          <div style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '10px', padding: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ProfileAvatar empCode={currentUser?.empCode} fullName={currentUser?.fullName} size={34} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentUser?.nameTh || currentUser?.fullName || '-'}
                </div>
                <div style={{ color: 'rgba(255,255,255,.55)', fontSize: '11.5px' }}>
                  {ROLE_LABELS[currentUser?.role] || currentUser?.role || '-'}
                </div>
              </div>
            </div>
            {(currentUser?.empCode || currentUser?.position || currentUser?.organization) && (
              <div style={{ marginTop: '10px', fontSize: '11px', color: 'rgba(255,255,255,.5)', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {currentUser.empCode && <div>รหัส: {currentUser.empCode}</div>}
                {currentUser.position && <div>ตำแหน่ง: {currentUser.position}</div>}
                {currentUser.organization?.level2 && <div>สาขา: {currentUser.organization.level2}</div>}
                {currentUser.organization?.level3 && <div>ฝ่าย: {currentUser.organization.level3}</div>}
                {currentUser.organization?.level4 && <div>แผนก: {currentUser.organization.level4}</div>}
              </div>
            )}
          </div>
        </div>

        {navigation.map((section) => {
          const visibleItems = section.items.filter((item) => isNavItemVisible(item.page));
          if (visibleItems.length === 0) return null;

          return (
            <div className="nav-section" key={section.label}>
              <div className="nav-label">{section.label}</div>
              {visibleItems.map((item) => (
                <NavItem key={item.page} page={item.page} icon={item.icon} title={item.title} />
              ))}
            </div>
          );
        })}

        <div style={{ padding: '8px 20px' }}>
          <div style={{ background: 'rgba(255,255,255,.05)', borderRadius: '6px', padding: '8px 10px', fontSize: '10.5px', color: 'rgba(255,255,255,.3)', lineHeight: 1.6 }}>
            <i className="fa-solid fa-keyboard" style={{ marginRight: '4px' }}></i> <span style={{ background: 'rgba(255,255,255,.1)', padding: '1px 4px', borderRadius: '3px', fontFamily: "'IBM Plex Mono',monospace" }}>/</span> ค้นหา ·{' '}
            <span style={{ background: 'rgba(255,255,255,.1)', padding: '1px 4px', borderRadius: '3px', fontFamily: "'IBM Plex Mono',monospace" }}>1-4</span> สลับหน้า
          </div>
        </div>
      </div>

      <div className="sidebar-footer">
        <div style={{ padding: '0 0 10px', fontSize: '10.5px', color: 'rgba(255,255,255,.3)', fontFamily: "'IBM Plex Mono',monospace", display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>api-local</span>
          <span>{clock}</span>
        </div>
        <div className="user-chip">
          <ProfileAvatar empCode={currentUser?.empCode} fullName={currentUser?.fullName} size={28} />
          <div className="user-info">
            <div className="user-name">{currentUser?.nameTh || currentUser?.fullName || '-'}</div>
            <div className="user-role">{currentUser?.organization?.level4 || currentUser?.department || '-'}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={logout}>ออก</button>
        </div>
      </div>
    </aside>
  );
}
